import { formatDate, secondsSinceMidnightToTimeString } from '@/helpers/dateHelper';
import { strings } from '@/services/translation/strings';
import type { ScheduleChangeRequest } from '@/services/workingPatterns/useWorkingPatternRequests';

interface GroupedScheduleChangesProps {
  requests: ScheduleChangeRequest[];
}

const formatTime = (value?: number) => (value == null ? 'Not scheduled' : secondsSinceMidnightToTimeString(value));

export const TeamMemberGroupedScheduleChangesCard = ({ requests }: GroupedScheduleChangesProps) => {
  const grouped = requests
    .slice()
    .sort((a, b) => (a.effectiveFrom ?? '').localeCompare(b.effectiveFrom ?? ''))
    .reduce(
      (acc, request) => {
        const date = request.effectiveFrom ?? 'Unknown';
        if (!acc[date]) acc[date] = [];
        acc[date].push(request);
        return acc;
      },
      {} as Record<string, ScheduleChangeRequest[]>
    );

  return (
    <div className="space-y-8">
      {Object.entries(grouped).map(([date, dateRequests]) => (
        <div key={date} className="space-y-4">
          <h3 className="font-semibold text-lg border-b pb-2">Effective from {formatDate(date)}</h3>
          {/* "Submitted" belongs to the request, not the day change, so it must live in this outer
          map — nesting it inside the dayChanges map below would repeat it once per changed day. */}
          {dateRequests.map(request => (
            <div key={request.id}>
              {request.requestedOn && (
                <p className="text-sm text-gray-500">Submitted {formatDate(request.requestedOn)}</p>
              )}

              {request.dayChanges?.map(change => (
                <div
                  key={`${request.id}-${change.dayNumber}-${change.slotNumber}`}
                  className="flex flex-row space-x-32"
                >
                  <div className="w-64">
                    <p>{strings.timeshift.teamMember.requests.scheduleUpdate.hoursBefore(change.day)}</p>
                    <p>
                      {formatTime(change.before?.startTime)} - {formatTime(change.before?.endTime)}
                    </p>
                  </div>
                  <div className="w-64">
                    <p>{strings.timeshift.teamMember.requests.scheduleUpdate.hoursAfter(change.day)}</p>
                    <p>
                      {formatTime(change.after?.startTime)} - {formatTime(change.after?.endTime)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
